import { isUserLoggedIn } from "@/utils/vue-helpers/user";
import { syncDexieOrders, unsyncedOrdersCount } from "@/db/orders";
import {
  syncDexieAnswerSyncs,
  unsyncedAnswerSyncsCount,
} from "@/db/answer-sync";
import { syncDexieReceiptCounters } from "@/db/receipt-counter";
import { syncDexieTransactions } from "@/db/transaction";
import {
  getPaymentService,
  isPaymentServiceReady,
} from "@/services/payment.service";
import { unsyncedPaymentsCount } from "@/db/payments";
import { dexieDB } from "./dexie-database";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { LogLevel, LogService } from "./logging";

// Lock keys — use localStorage to share locks across app windows (main + customer display)
const ORDER_SYNC_LOCK_KEY = "__SMEETZ_IS_SYNCING_ORDERS__";
const ANSWER_SYNC_LOCK_KEY = "__SMEETZ_IS_SYNCING_ANSWERS__";
const PAYMENT_SYNC_LOCK_KEY = "__SMEETZ_IS_SYNCING_PAYMENTS__";
const RECEIPT_COUNTERS_LOCK_KEY = "__SMEETZ_IS_SYNCING_RECEIPT_COUNTERS__";
const TRANSACTIONS_LOCK_KEY = "__SMEETZ_IS_SYNCING_TRANSACTIONS__";

const SYNC_SERVICE_INSTANCE_ID = Math.random().toString(36).slice(2);

export const ORDER_SYNC_BATCH_LIMIT = 3;
export const MAX_SYNC_ATTEMPTS = 10;

function getGlobalLock(key: string): boolean {
  return localStorage.getItem(key) === "true";
}

function setGlobalLock(key: string, v: boolean) {
  if (v) {
    localStorage.setItem(key, "true");
  } else {
    localStorage.removeItem(key);
  }
}

/**
 * Reset all sync locks. Call this on app startup to clear any stale locks.
 */
export function resetAllSyncLocks() {
  setGlobalLock(ORDER_SYNC_LOCK_KEY, false);
  setGlobalLock(ANSWER_SYNC_LOCK_KEY, false);
  setGlobalLock(PAYMENT_SYNC_LOCK_KEY, false);
  setGlobalLock(RECEIPT_COUNTERS_LOCK_KEY, false);
  setGlobalLock(TRANSACTIONS_LOCK_KEY, false);
}

/**
 * Initiate the sync job for completed orders awaiting persistence to the backend.
 */
export async function initiateOrderSync(): Promise<void> {
  LogService.info(
    `Sync: initiateOrderSync() instance=${SYNC_SERVICE_INSTANCE_ID}`
  );
  if (!navigator.onLine || !isUserLoggedIn.value) {
    LogService.info(
      "Sync: Aborted sync job due to offline or user not logged in",
      {
        online: navigator.onLine,
        userLoggedIn: isUserLoggedIn.value,
      }
    );
    return;
  }

  if (getGlobalLock(ORDER_SYNC_LOCK_KEY)) {
    LogService.info(`Sync: Aborted sync job because lock is active`, {
      instanceId: SYNC_SERVICE_INSTANCE_ID,
    });
    return;
  }

  setGlobalLock(ORDER_SYNC_LOCK_KEY, true);

  LogService.info("Sync: Initiating orders sync");
  await syncDexieOrders(ORDER_SYNC_BATCH_LIMIT);
  LogService.info("Sync: Completed orders sync");
  setGlobalLock(ORDER_SYNC_LOCK_KEY, false);

  const totalUnsynced = await unsyncedOrdersCount();

  // If there are more unsynced (Pending or Failed) orders, re-trigger right away
  if (totalUnsynced > 0) {
    setTimeout(initiateOrderSync, 1000);
  }
}

/**
 * Initiate sync for completed orders' custom-field answer + file uploads.
 */
export async function initiateAnswerSync(): Promise<void> {
  if (!navigator.onLine || !isUserLoggedIn.value) return;
  if (getGlobalLock(ANSWER_SYNC_LOCK_KEY)) return;

  setGlobalLock(ANSWER_SYNC_LOCK_KEY, true);
  try {
    await syncDexieAnswerSyncs(ORDER_SYNC_BATCH_LIMIT);
  } catch (error) {
    LogService.info(
      "Sync: Error during answer sync",
      { error },
      LogLevel.Error
    );
  } finally {
    setGlobalLock(ANSWER_SYNC_LOCK_KEY, false);
  }

  // Re-trigger if more remain (mirrors the orders queue).
  const remaining = await unsyncedAnswerSyncsCount();
  if (remaining > 0) {
    setTimeout(initiateAnswerSync, 1000);
  }
}

/**
 * Initiate sync for queued payments (failed POST/PATCH retries).
 * The payment package (`@smeetz/payment-common`) owns the drain via
 * `syncPaymentsFromQueue()`; it reads/deletes from our Dexie `payments` queue.
 */
export async function initiatePaymentSync(): Promise<void> {
  if (!navigator.onLine || !isUserLoggedIn.value) return;
  // No payment service without an open cash float — payments are only queued
  // while a till is open, so there is normally nothing to drain until it is
  // ready. Queued payments in that state are an anomaly worth surfacing.
  if (!isPaymentServiceReady()) {
    const queued = await unsyncedPaymentsCount();
    if (queued > 0) {
      LogService.info(
        `Sync: ${queued} queued payment(s) cannot sync, payment service not ready`,
        {},
        LogLevel.Warning
      );
    }
    return;
  }
  if (getGlobalLock(PAYMENT_SYNC_LOCK_KEY)) return;

  setGlobalLock(PAYMENT_SYNC_LOCK_KEY, true);
  try {
    await getPaymentService().syncPaymentsFromQueue();
  } catch (error) {
    LogService.info(
      "Sync: Error during payment sync",
      { error },
      LogLevel.Error
    );
  } finally {
    setGlobalLock(PAYMENT_SYNC_LOCK_KEY, false);
  }
}

/**
 * Initiate sync for receipt counter payloads (failed PATCH retries)
 */
export async function initiateReceiptCountersSync(): Promise<void> {
  if (!navigator.onLine || !isUserLoggedIn.value) return;
  if (getGlobalLock(RECEIPT_COUNTERS_LOCK_KEY)) return;

  setGlobalLock(RECEIPT_COUNTERS_LOCK_KEY, true);
  try {
    await syncDexieReceiptCounters();
  } catch (error) {
    LogService.info(
      "Sync: Error during receipt counters sync",
      { error },
      LogLevel.Error
    );
  } finally {
    setGlobalLock(RECEIPT_COUNTERS_LOCK_KEY, false);
  }
}

/**
 * Initiate sync for Fiskaly transactions
 */
export async function initiateTransactionsSync(): Promise<void> {
  if (!navigator.onLine || !isUserLoggedIn.value) return;
  if (getGlobalLock(TRANSACTIONS_LOCK_KEY)) return;

  setGlobalLock(TRANSACTIONS_LOCK_KEY, true);
  try {
    await syncDexieTransactions();
  } catch (error) {
    LogService.info(
      "Sync: Error during transactions sync",
      { error },
      LogLevel.Error
    );
  } finally {
    setGlobalLock(TRANSACTIONS_LOCK_KEY, false);
  }

  // Check if there are remaining unsynced transactions and re-trigger
  try {
    const remaining = await dexieDB.untypedTransactions
      .where("syncStatus")
      .anyOf([SyncStatus.Pending, SyncStatus.Failed])
      .count();
    if (remaining > 0) {
      setTimeout(initiateTransactionsSync, 1000);
    }
  } catch {
    // ignore
  }
}
